import isEqual from 'lodash/isEqual';

import { DateFormat, formatDate } from '@lyearn/core/utils/date/formatDate';
import { formatRange, RangeFormat } from '@lyearn/core/utils/date/formatRange';
import { TFunction } from '@lyearn/i18n';
import {
  DateFilterListItem,
  DateFilterListItemKey,
  DateFilterListItems,
} from '@/components/DateFilter/constants';
import { DateRange } from '@/components/DateFilter/types';
import { FilterOperators } from '@/components/Filter/types';
import {
  AbsoluteDateFilter,
  DateFilter,
  RelativeDateFilterOperatorEnum,
  RelativeDateFilterUnitEnum,
} from '@/types/schema';

import FilterLabelWithOperator from '../EntityFilter/components/FilterLabelWithOperator';

export const DATE_FILTER_OPTIONS_VALUE = {
  [DateFilterListItemKey.Last7Days]: {
    relative: {
      value: 7,
      operator: RelativeDateFilterOperatorEnum.Last,
      unit: RelativeDateFilterUnitEnum.Day,
    },
  },
  [DateFilterListItemKey.Last30Days]: {
    relative: {
      value: 30,
      operator: RelativeDateFilterOperatorEnum.Last,
      unit: RelativeDateFilterUnitEnum.Day,
    },
  },
  [DateFilterListItemKey.Last3Months]: {
    relative: {
      value: 3,
      operator: RelativeDateFilterOperatorEnum.Last,
      unit: RelativeDateFilterUnitEnum.Month,
    },
  },
  [DateFilterListItemKey.Last6Months]: {
    relative: {
      value: 6,
      operator: RelativeDateFilterOperatorEnum.Last,
      unit: RelativeDateFilterUnitEnum.Month,
    },
  },
  [DateFilterListItemKey.LastYear]: {
    relative: {
      value: 12,
      operator: RelativeDateFilterOperatorEnum.Last,
      unit: RelativeDateFilterUnitEnum.Month,
    },
  },
  [DateFilterListItemKey.Next1DayFromNow]: {
    relative: {
      value: 1,
      operator: RelativeDateFilterOperatorEnum.Now,
      unit: RelativeDateFilterUnitEnum.Day,
    },
  },
  [DateFilterListItemKey.Next2DaysFromNow]: {
    relative: {
      value: 2,
      operator: RelativeDateFilterOperatorEnum.Now,
      unit: RelativeDateFilterUnitEnum.Day,
    },
  },
  [DateFilterListItemKey.Next7DaysFromNow]: {
    relative: {
      value: 7,
      operator: RelativeDateFilterOperatorEnum.Now,
      unit: RelativeDateFilterUnitEnum.Day,
    },
  },
  [DateFilterListItemKey.Next30DaysFromNow]: {
    relative: {
      value: 30,
      operator: RelativeDateFilterOperatorEnum.Now,
      unit: RelativeDateFilterUnitEnum.Day,
    },
  },
  [DateFilterListItemKey.NextYear]: {
    relative: {
      value: 1,
      operator: RelativeDateFilterOperatorEnum.Next,
      unit: RelativeDateFilterUnitEnum.Year,
    },
  },
  [DateFilterListItemKey.ThisYear]: {
    relative: {
      operator: RelativeDateFilterOperatorEnum.This,
      unit: RelativeDateFilterUnitEnum.Year,
    },
  },
  [DateFilterListItemKey.Today]: {
    relative: {
      operator: RelativeDateFilterOperatorEnum.This,
      unit: RelativeDateFilterUnitEnum.Day,
    },
  },
  [DateFilterListItemKey.Tomorrow]: {
    relative: {
      operator: RelativeDateFilterOperatorEnum.Next,
      unit: RelativeDateFilterUnitEnum.Day,
    },
  },
  [DateFilterListItemKey.Yesterday]: {
    relative: {
      operator: RelativeDateFilterOperatorEnum.Previous,
      unit: RelativeDateFilterUnitEnum.Day,
    },
  },
  [DateFilterListItemKey.ThisWeek]: {
    relative: {
      operator: RelativeDateFilterOperatorEnum.This,
      unit: RelativeDateFilterUnitEnum.Week,
    },
  },
  [DateFilterListItemKey.LastWeek]: {
    relative: {
      operator: RelativeDateFilterOperatorEnum.Previous,
      unit: RelativeDateFilterUnitEnum.Week,
    },
  },
  [DateFilterListItemKey.NextWeek]: {
    relative: {
      operator: RelativeDateFilterOperatorEnum.Next,
      unit: RelativeDateFilterUnitEnum.Week,
    },
  },
  [DateFilterListItemKey.ThisMonth]: {
    relative: {
      operator: RelativeDateFilterOperatorEnum.This,
      unit: RelativeDateFilterUnitEnum.Month,
    },
  },
  [DateFilterListItemKey.LastMonth]: {
    relative: {
      operator: RelativeDateFilterOperatorEnum.Previous,
      unit: RelativeDateFilterUnitEnum.Month,
    },
  },
  [DateFilterListItemKey.Overdue]: {
    relative: undefined,
  },
  [DateFilterListItemKey.Custom]: {
    relative: undefined,
  },
};

export const getSelectedRelativeValue = (
  selectedValue: DateFilter,
  options = DateFilterListItems,
) => {
  return options.find((item) =>
    isEqual(selectedValue.relative, DATE_FILTER_OPTIONS_VALUE[item.key].relative),
  );
};

interface GetDateFilterLabelParams {
  t: TFunction;
  defaultLabel: string;
  selectedValue?: DateFilter;
  options?: DateFilterListItem[];
  selectedOperator?: FilterOperators;
}

function getOverdueLabel(t: TFunction, options?: DateFilterListItem[]) {
  const label = options?.find((option) => option.key === DateFilterListItemKey.Overdue)?.label;
  return label ? t(label) : undefined;
}

function isOverdue(absoluteDate: AbsoluteDateFilter) {
  // TODO: To be handled using relative filters from BE
  // https://linear.app/lyearn/issue/ENG-5492/due-date-filter-overdue-handle-using-relative-filters
  const { startTime, endTime } = absoluteDate;
  return !startTime && endTime;
}

export function getDateFilterLabel(params: GetDateFilterLabelParams) {
  const { t, defaultLabel, selectedValue, options, selectedOperator: operator } = params;

  if (!selectedValue) {
    return defaultLabel;
  }

  if (selectedValue.relative) {
    const selectedOption = getSelectedRelativeValue(selectedValue, options);
    if (selectedOption) {
      const value = t(selectedOption.label);

      return operator ? (
        <FilterLabelWithOperator entityLabel={defaultLabel} operator={operator} value={[value]} />
      ) : (
        value
      );
    }
  }

  if (selectedValue.absolute) {
    const { startTime, endTime } = selectedValue.absolute;

    let value;
    if (isOverdue(selectedValue.absolute)) {
      value = getOverdueLabel(t, options);
    } else if (startTime && endTime) {
      value = formatRange(new Date(startTime), new Date(endTime), RangeFormat.Date, {
        includeYear: true,
      });
    }

    if (value) {
      return operator ? (
        <FilterLabelWithOperator entityLabel={defaultLabel} operator={operator} value={[value]} />
      ) : (
        value
      );
    }
  }

  return defaultLabel;
}

export const getSelectedValues = (selectedValue?: DateFilter, options?: DateFilterListItem[]) => {
  if (!selectedValue) {
    return {
      dateRange: undefined,
      selectedOption: undefined,
    };
  }

  if (selectedValue.relative) {
    const selectedOption = getSelectedRelativeValue(selectedValue, options);
    return {
      dateRange: undefined,
      selectedOption,
    };
  }

  if (selectedValue.absolute) {
    const { startTime, endTime } = selectedValue.absolute;
    if (isOverdue(selectedValue.absolute)) {
      return {
        dateRange: { endTime: new Date(endTime!) } as DateRange,
        selectedOption: options?.find((option) => option.key === DateFilterListItemKey.Overdue),
      };
    }
    if (startTime && endTime) {
      return {
        dateRange: { startTime: new Date(startTime), endTime: new Date(endTime) },
        selectedOption: undefined,
      };
    }
  }

  return {
    dateRange: undefined,
    selectedOption: undefined,
  };
};

export function getSimpleDateFilterLabel(params: DateFilter) {
  if (params.absolute) {
    const { startTime, endTime } = params.absolute;
    if (startTime && endTime) {
      return formatRange(new Date(startTime), new Date(endTime), RangeFormat.Date, {
        includeYear: true,
      });
    } else if (startTime) {
      return `After ${formatDate(new Date(startTime), DateFormat.Date, { includeYear: true })}`;
    } else if (endTime) {
      return `Before ${formatDate(new Date(endTime), DateFormat.Date, { includeYear: true })}`;
    } else {
      return `Unknown date range`;
    }
  } else if (params.relative) {
    const { operator, unit, value } = params.relative;
    const formattedUnit =
      unit.toLowerCase() + (value !== null && value !== undefined && value > 1 ? 's' : '');
    switch (operator) {
      case RelativeDateFilterOperatorEnum.Last:
        return `Last ${value} ${formattedUnit}`;
      case RelativeDateFilterOperatorEnum.Next:
        return `Next ${value} ${formattedUnit}`;
      case RelativeDateFilterOperatorEnum.Now:
        return `Next ${value} ${formattedUnit} from now`;
      case RelativeDateFilterOperatorEnum.This:
        return `This ${formattedUnit}`;
      case RelativeDateFilterOperatorEnum.Previous:
        return `Previous ${formattedUnit}`;
      default:
        return 'Unknown date range';
    }
  } else {
    return 'No date range';
  }
}
